import { Flex, Form, Select, Skeleton } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import type { BaseOptionType } from "antd/lib/select"
import React, { Suspense, lazy, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { fetchLabelById } from "../../../SettingsPermissions/redux/permissions.thunks.ts.ts"
import type { TeamMemberMutationData } from "../../types/types.ts"

const TeamMemberFormPermissions = lazy(() =>
  import("./TeamMemberFormPermissions.tsx").then((mod) => ({
    default: mod.TeamMemberFormPermissions
  }))
)

interface TeamMemberPermissionsRowProps {
  labelOptions: BaseOptionType[]
  form: FormInstance<TeamMemberMutationData>
  saving: boolean
  modifyPermissionsAllowed?: boolean
  isSelfEdit?: boolean
  isGccOwner?: boolean
}

export function TeamMemberPermissionsRow({
  form,
  labelOptions,
  saving,
  modifyPermissionsAllowed,
  isSelfEdit,
  isGccOwner
}: TeamMemberPermissionsRowProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [fetching, setFetching] = useState(false)

  const individualPermissionsDisabledReason = isSelfEdit
    ? t("permissions.individualPermissionsSelf")
    : isGccOwner
      ? t("permissions.individualPermissionsOwner")
      : undefined

  const handleGetPermissionsByLabelId = useCallback(
    async (labelId: string | number) => {
      try {
        setFetching(true)

        const res = await dispatch(fetchLabelById(labelId)).unwrap()
        const reqPermissions = res?.permissions

        form.setFieldValue("permissions", reqPermissions)
      } finally {
        setFetching(false)
      }
    },
    [dispatch, form]
  )

  return (
    <Flex gap={16}>
      <Form.Item<TeamMemberMutationData>
        label="Role"
        name="label_id"
        className={"w-full"}
        rules={[{ required: true, message: "Please select role" }]}
      >
        <Select
          size="large"
          loading={fetching}
          disabled={fetching || isSelfEdit || saving || isGccOwner}
          placeholder={"Select role"}
          onSelect={handleGetPermissionsByLabelId}
          options={labelOptions}
        />
      </Form.Item>

      {modifyPermissionsAllowed && (
        <Suspense fallback={<Skeleton.Button active />}>
          <TeamMemberFormPermissions
            form={form}
            saving={saving}
            fetching={fetching}
            disabledReason={individualPermissionsDisabledReason}
            // role={initialValues?.role}
            // getDefaultPermissions={handleGetDefaultPermissions}
          />
        </Suspense>
      )}
    </Flex>
  )
}
